import { giphyBlue, giphyPink } from '@giphy/colors'
import { IUser } from '@giphy/js-types'
import { useContext } from 'react'
import { ILoggedInUser } from 'types'
import { HeaderContext } from '../header'
import MenuComponent from './menu'
import UserMenu from './user-menu'

export type UserMenuItem = {
    path: string
    label: string
    permission?: string
}

export type MainMenuLink = {
    title: string
    link: string
    newPill?: boolean
}
export type MainMenuItem = {
    header: MainMenuLink
    items: (MainMenuLink | string)[]
}

type Props = { onItemClick: (path: string) => void }
const Menu = ({ onItemClick }: Props) => {
    const { menu, user } = useContext(HeaderContext)
    switch (menu) {
        case 'logged out user':
            return <UserMenu color={giphyBlue} items={loggedOutUserItems} itemClick={onItemClick} />
        case 'user':
            return user ? <UserMenu color={giphyBlue} items={userMenuItems(user)} itemClick={onItemClick} /> : null
        case 'category':
            return <MenuComponent color={giphyPink} items={mainMenuItems(user ?? undefined)} />
        default:
            return null
    }
}

const loggedOutUserItems: UserMenuItem[] = [
    { path: '/login', label: 'Login' },
    { path: '/join', label: 'Join' },
    { path: '/favorites', label: 'Favorites' },
]

const userMenuItems = (user: IUser): UserMenuItem[] => {
    const loggedInUser = user as ILoggedInUser
    const { channel, permissions, is_upgraded } = loggedInUser
    const items: UserMenuItem[] = [
        {
            path: '/favorites',
            label: 'Favorites',
        },
        {
            path: channel,
            label: 'My Channel',
        },
        {
            path: '/settings',
            label: 'Settings',
        },
        ...(!is_upgraded ? [{ path: '/apply', label: 'Upgrade Account' }] : []),
        {
            path: '/logout',
            label: 'Log Out',
        },
    ]

    const gatedItems = [
        {
            path: '/dashboard',
            label: 'Dashboard',
            permission: 'dashboard_analytics',
        },
    ]

    // @ts-ignore TODO type this
    const filteredGatedItems = gatedItems.filter((item) => permissions[item.permission])
    return [items[0], ...filteredGatedItems, ...items.slice(1)]
}

const getItem =
    (prefix: string, joiner = '') =>
    (title: string | object): MainMenuLink | string =>
        title instanceof Object
            ? (title as unknown as string)
            : {
                  title,
                  link: `${prefix}${encodeURIComponent(title.replace(' ', joiner || ''))}`.toLowerCase(),
              }

export const mainMenuItems = (user?: IUser): MainMenuItem[] => [
    {
        header: { title: 'Advertise', link: 'https://ads.giphy.com' },
        items: [
            {
                title: 'Case Studies',
                link: 'https://ads.giphy.com#comp-m862ifvu',
            },
            {
                title: 'Work with Us',
                link: 'https://ads.giphy.com',
            },
        ],
    },
    {
        header: { title: 'Categories', link: '/categories' },
        items: [
            { title: `Entertainment`, link: `/categories/entertainment` },
            { title: `Sports`, link: `/categories/sports` },
            { title: `News & Politics`, link: `/categories/news-politics` },
            { title: `Anime`, link: `/categories/anime` },
            { title: `Memes`, link: `/categories/memes` },
            { title: `Holidays`, link: `/categories/holidays` },
            { title: `Gaming`, link: `/categories/gaming` },
            { title: `Emotions`, link: `/categories/emotions` },
            { title: `Reactions`, link: `/categories/reactions` },
            { title: `Weird`, link: `/categories/weird` },
        ],
    },
    {
        header: { title: 'About', link: '/about' },
        items: [
            {
                title: 'Download the App',
                link: 'https://apps.apple.com/us/app/giphy-the-gif-search-engine/id974748812',
            },
            {
                title: 'Community Guidelines',
                link: '/community-guidelines',
            },
            ...(!user || !(user as ILoggedInUser).is_upgraded
                ? [
                      {
                          title: 'Upgrade Account',
                          link: '/apply',
                      },
                  ]
                : []),
            {
                title: 'Transparency',
                link: '/transparency/2025',
            },
            {
                title: 'Support & FAQ',
                link: 'https://support.giphy.com/hc/en-us',
            },
            {
                title: 'Hire a GIF Artist',
                link: 'https://support.giphy.com/hc/en-us/articles/360022661172-How-to-Hire-an-Artist-on-GIPHY',
            },
            {
                title: 'DMCA',
                link: 'https://support.giphy.com/hc/en-us/articles/360020287091-GIPHY-DMCA-Copyright-Policy',
            },
            {
                title: 'Jobs',
                link: 'https://careers.shutterstock.com/us/en/search-results?keywords=giphy',
            },
        ].map(getItem('/')),
    },
]

export default Menu
